import { type PlanLimits, resolveLimits } from '@qr/shared/plans';
import { PLANS, hidesBillingUi, type PlanId } from '@qr/shared/plans';
import { usageMonthKey } from '@qr/shared/paths';
import type { Role } from '@qr/shared/types';
import {
  collectionGroup,
  doc,
  onSnapshot,
  query,
  setDoc,
  where,
} from 'firebase/firestore';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useAuth } from './auth.js';
import { db } from './firebase.js';

/**
 * Espacios de trabajo del usuario y cual esta abierto.
 *
 * Una persona puede pertenecer a varios: el suyo propio, que se crea al darse
 * de alta y existe siempre, y los equipos a los que la hayan invitado. Es el
 * mismo modelo de GitHub o Firebase, donde tu cuenta personal no desaparece
 * porque te inviten a un sitio.
 *
 * La lista sale de los documentos de miembro, que son la fuente de verdad. El
 * campo `lastActiveOrgId` del perfil es solo una preferencia para no preguntar
 * en cada visita; si apunta a un espacio del que ya no se forma parte, se
 * ignora y se abre el primero disponible.
 */

export interface Membership {
  readonly orgId: string;
  readonly role: Role;
}

export interface OrgSummary {
  readonly orgId: string;
  readonly name: string;
  readonly role: Role;
  readonly isOwn: boolean;
}

export interface OrgState {
  readonly orgId: string | null;
  readonly name: string;
  readonly plan: PlanId;
  readonly planName: string;
  readonly limits: PlanLimits;
  readonly role: Role | null;
  readonly status: 'active' | 'suspended';
  /** Cuenta de cortesia: se ocultan medidores y avisos de plan. */
  readonly isComped: boolean;
  readonly scansThisMonth: number;
  readonly loading: boolean;
  /** Todos los espacios a los que pertenece, para el selector. */
  readonly memberships: readonly Membership[];
  readonly switchTo: (orgId: string) => Promise<void>;
}

const OrgContext = createContext<OrgState | null>(null);

type InternalState = Omit<OrgState, 'memberships' | 'switchTo'>;

const INITIAL: InternalState = {
  orgId: null,
  name: '',
  plan: 'free',
  planName: PLANS.free.name,
  limits: PLANS.free.limits,
  role: null,
  status: 'active',
  isComped: false,
  scansThisMonth: 0,
  loading: true,
};

export function OrgProvider({ children }: { readonly children: ReactNode }) {
  const { user } = useAuth();
  const [state, setState] = useState(INITIAL);
  const [memberships, setMemberships] = useState<Membership[]>([]);
  const [preferred, setPreferred] = useState<string | null>(null);
  const [membershipsReady, setMembershipsReady] = useState(false);

  // 1. A que espacios pertenece. Es la fuente de verdad.
  useEffect(() => {
    if (!user) {
      setMemberships([]);
      setMembershipsReady(false);
      setState(INITIAL);
      return;
    }

    const q = query(collectionGroup(db, 'members'), where('uid', '==', user.uid));

    return onSnapshot(
      q,
      (snap) => {
        setMemberships(
          snap.docs.map((d) => ({
            orgId: (d.data()['orgId'] as string) ?? d.ref.parent.parent?.id ?? '',
            role: (d.data()['role'] as Role) ?? 'viewer',
          })),
        );
        setMembershipsReady(true);
      },
      (caught) => {
        // Tipico aqui: falta el indice de grupo de colecciones, o las reglas
        // deniegan la consulta. Sin este registro, el sintoma es un panel
        // vacio sin ninguna pista de por que.
        console.error('No se pudieron cargar tus negocios', caught);
        setMemberships([]);
        setMembershipsReady(true);
      },
    );
  }, [user]);

  // 2. Cual prefiere tener abierto.
  useEffect(() => {
    if (!user) {
      setPreferred(null);
      return;
    }
    return onSnapshot(
      doc(db, `users/${user.uid}`),
      (snap) => setPreferred((snap.data()?.['lastActiveOrgId'] as string | null) ?? null),
      (caught) => {
        console.error('No se pudo leer tu perfil', caught);
        setPreferred(null);
      },
    );
  }, [user]);

  /**
   * El espacio activo: el preferido si sigue siendo suyo, y si no el primero
   * que tenga. La comprobacion no es paranoia: alguien puede haber abandonado
   * un equipo o haber sido expulsado desde otro dispositivo, y sin esto el
   * panel intentaria abrir un espacio al que ya no tiene acceso y se quedaria
   * en blanco sin explicar por que.
   */
  const activeOrgId = useMemo(() => {
    if (!membershipsReady) return null;

    /**
     * Respaldo para las cuentas anteriores al selector.
     *
     * Sus documentos de miembro se escribieron sin el campo `uid`, asi que la
     * consulta por grupo de colecciones no los encuentra y la lista sale
     * vacia. Sin este respaldo, el panel se quedaria en blanco para quien ya
     * tenia cuenta: ni codigos, ni equipo, ni forma de salir del atasco.
     *
     * Se apoya en la preferencia guardada, que esas cuentas si tienen. No
     * concede nada: las reglas siguen exigiendo pertenecer a la organizacion
     * para leer cualquiera de sus datos, asi que si el respaldo apuntara a un
     * sitio ajeno, la lectura fallaria igual.
     *
     * Se puede retirar cuando ya no queden cuentas sin migrar.
     */
    if (memberships.length === 0) return preferred;

    const stillAMember = memberships.some((m) => m.orgId === preferred);
    return stillAMember ? preferred : (memberships[0]?.orgId ?? null);
  }, [memberships, membershipsReady, preferred]);

  // 3. Los datos del espacio abierto, en vivo.
  useEffect(() => {
    if (!user) return;

    if (!activeOrgId) {
      // Sin espacios todavia: puede pasar justo tras el alta, antes de que la
      // funcion bloqueante termine de escribir.
      if (membershipsReady) setState((prev) => ({ ...prev, loading: false }));
      return;
    }

    const unsubOrg = onSnapshot(
      doc(db, `orgs/${activeOrgId}`),
      (snap) => {
        const data = snap.data();
        if (!data) {
          setState((prev) => ({ ...prev, loading: false }));
          return;
        }
        const plan = (data['plan'] as PlanId) ?? 'free';
        setState((prev) => ({
          ...prev,
          orgId: activeOrgId,
          name: (data['name'] as string) ?? '',
          plan,
          planName: PLANS[plan].name,
          limits: resolveLimits(plan, data['planOverrides'] ?? null),
          status: (data['status'] as 'active' | 'suspended') ?? 'active',
          isComped: hidesBillingUi(plan),
          loading: false,
        }));
      },
      (caught) => {
        // Si esto falla, el panel se queda cargando para siempre. Al menos que
        // deje de girar la rueda y quede constancia de por que.
        console.error('No se pudo cargar el negocio', caught);
        setState((prev) => ({ ...prev, loading: false }));
      },
    );

    const unsubMember = onSnapshot(
      doc(db, `orgs/${activeOrgId}/members/${user.uid}`),
      (snap) => {
        setState((prev) => ({ ...prev, role: (snap.data()?.['role'] as Role) ?? null }));
      },
    );

    const unsubUsage = onSnapshot(
      doc(db, `orgs/${activeOrgId}/usage/${usageMonthKey()}`),
      (snap) => {
        setState((prev) => ({
          ...prev,
          scansThisMonth: (snap.data()?.['scans'] as number | undefined) ?? 0,
        }));
      },
    );

    return () => {
      unsubOrg();
      unsubMember();
      unsubUsage();
    };
  }, [user, activeOrgId, membershipsReady]);

  const switchTo = useCallback(
    async (orgId: string) => {
      if (!user) return;
      // La preferencia la escribe el propio cliente: es suya y las reglas solo
      // la aceptan si de verdad pertenece a ese espacio.
      await setDoc(doc(db, `users/${user.uid}`), { lastActiveOrgId: orgId }, { merge: true });
    },
    [user],
  );

  const value = useMemo<OrgState>(
    () => ({ ...state, orgId: activeOrgId, memberships, switchTo }),
    [state, activeOrgId, memberships, switchTo],
  );

  return <OrgContext.Provider value={value}>{children}</OrgContext.Provider>;
}

export function useOrg(): OrgState {
  const context = useContext(OrgContext);
  if (!context) throw new Error('useOrg debe usarse dentro de <OrgProvider>.');
  return context;
}

/** Comprueba si el rol actual alcanza el minimo pedido. */
export function useCan(minimum: Role): boolean {
  const { role } = useOrg();
  const rank = { owner: 4, admin: 3, editor: 2, viewer: 1 } as const;
  return role !== null && rank[role] >= rank[minimum];
}
